import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import { Button } from "../../../components/common";
import {
  useAppDispatch,
  useAppSelector,
  getChatInboxThunk,
  getChatMessagesThunk,
  sendChatMessageThunk,
  setActiveOtherPartyId,
  getProfileThunk,
} from "../../../redux";
import { showSuccess, showError } from "../../../services";
import { formatChatMessageTime } from "../../../utils";
import refreshIcon from "../../../assets/icons/refresh.svg";
import "../../Teacher/pages/TeacherInbox.css";

const StudentInbox: React.FC = () => {
  const dispatch = useAppDispatch();
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const initialScrolledPartiesRef = useRef<Record<string, boolean>>({});
  const shouldScrollToBottomRef = useRef<boolean>(false);
  const prevScrollHeightRef = useRef<number>(0);
  const prevScrollTopRef = useRef<number>(0);
  const isPrependingRef = useRef<boolean>(false);

  const { user } = useAppSelector((state) => state.auth);
  const {
    conversations,
    activeOtherPartyId,
    messagesByParty,
    hasMoreByParty,
    isFetchingOlderByParty,
    isLoading: isChatLoading,
  } = useAppSelector((state) => state.chat);

  const [messageInput, setMessageInput] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");

  useEffect(() => {
    dispatch(getChatInboxThunk({ force: true }));
    if (!user) {
      dispatch(getProfileThunk());
    }
  }, [dispatch, user]);

  const assignedTeacher = user?.Allocated_Teacher;
  const assignedTeacherId = assignedTeacher?.id;
  const assignedTeacherName = assignedTeacher?.name;

  const teacherThread = useMemo(() => {
    if (!assignedTeacherId || !assignedTeacherName) return null;
    const matchingConv = conversations.find((c) => c.otherPartyId === assignedTeacherId);
    return {
      otherPartyId: assignedTeacherId,
      otherPartyName: assignedTeacherName,
      lastMessage: matchingConv?.lastMessage || "",
      lastMessageAt: matchingConv?.lastMessageAt || "",
      unreadCount: matchingConv?.unreadCount || 0,
    };
  }, [assignedTeacherId, assignedTeacherName, conversations]);

  const isMatchingSearch = useMemo(() => {
    if (!teacherThread) return false;
    const q = searchQuery.toLowerCase().trim();
    if (!q) return true;
    return (
      teacherThread.otherPartyName.toLowerCase().includes(q) ||
      teacherThread.otherPartyId.toLowerCase().includes(q)
    );
  }, [teacherThread, searchQuery]);

  useEffect(() => {
    if (assignedTeacherId && activeOtherPartyId !== assignedTeacherId) {
      dispatch(setActiveOtherPartyId(assignedTeacherId));
    }
  }, [assignedTeacherId, activeOtherPartyId, dispatch]);

  const activeThread = teacherThread;
  const activeMessages = activeOtherPartyId
    ? messagesByParty[activeOtherPartyId] || []
    : [];
  const isFetchingOlder = activeOtherPartyId
    ? !!isFetchingOlderByParty[activeOtherPartyId]
    : false;
  const hasMore = activeOtherPartyId
    ? hasMoreByParty[activeOtherPartyId] !== false
    : true;
  const isLoading = isChatLoading;

  const handleRefresh = () => {
    dispatch(getProfileThunk({ force: true }));
    dispatch(getChatInboxThunk({ force: true }));
    if (activeOtherPartyId) {
      initialScrolledPartiesRef.current[activeOtherPartyId] = false;
      dispatch(
        getChatMessagesThunk({ recipientId: activeOtherPartyId, force: true }),
      );
    }
  };

  useEffect(() => {
    if (activeOtherPartyId) {
      dispatch(getChatMessagesThunk({ recipientId: activeOtherPartyId, force: true }));
    }
  }, [dispatch, activeOtherPartyId]);

  useLayoutEffect(() => {
    const container = chatContainerRef.current;
    if (!container || !activeOtherPartyId) return;

    if (isPrependingRef.current) {
      container.scrollTop =
        prevScrollTopRef.current +
        (container.scrollHeight - prevScrollHeightRef.current);
      isPrependingRef.current = false;
    } else if (
      shouldScrollToBottomRef.current ||
      (!initialScrolledPartiesRef.current[activeOtherPartyId] &&
        activeMessages.length > 0)
    ) {
      container.scrollTop = container.scrollHeight;
      requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight;
      });
      initialScrolledPartiesRef.current[activeOtherPartyId] = true;
      shouldScrollToBottomRef.current = false;
    }
  }, [activeMessages, activeOtherPartyId, activeThread]);

  const handleScroll = () => {
    const container = chatContainerRef.current;
    if (!container || !activeOtherPartyId) return;

    if (
      container.scrollTop <= 20 &&
      hasMore &&
      !isFetchingOlder &&
      !isLoading
    ) {
      prevScrollHeightRef.current = container.scrollHeight;
      prevScrollTopRef.current = container.scrollTop;
      isPrependingRef.current = true;

      dispatch(
        getChatMessagesThunk({
          recipientId: activeOtherPartyId,
          offset: activeMessages.length,
          limit: 10,
          isOlder: true,
        }),
      );
    }
  };

  const handleSelectConversation = (otherPartyId: string) => {
    dispatch(setActiveOtherPartyId(otherPartyId));
    setMessageInput("");
  };

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = messageInput.trim();

    if (!trimmed) {
      showError("Please enter a message to send.");
      return;
    }

    if (!activeOtherPartyId) return;

    shouldScrollToBottomRef.current = true;
    dispatch(
      sendChatMessageThunk({
        recipientId: activeOtherPartyId,
        message: trimmed,
      }),
    ).then((res) => {
      if (sendChatMessageThunk.fulfilled.match(res)) {
        showSuccess("Message sent successfully!");
        setMessageInput("");
      }
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <div className="chat-container-card">
        {/* Sidebar Conversation List */}
        <div className="chat-sidebar">
          <div className="chat-sidebar-header">
            <h3>Teacher</h3>
            <button
              type="button"
              className="chat-refresh-btn"
              onClick={handleRefresh}
              title="Refresh conversations"
              aria-label="Refresh conversations"
            >
              <img
                src={refreshIcon}
                alt="Refresh"
                className={`chat-refresh-icon ${isLoading ? "spinning" : ""}`}
              />
            </button>
          </div>

          <div className="chat-search-wrapper">
            <input
              type="text"
              placeholder="Search teacher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="chat-search-input"
            />
          </div>

          <div className="chat-threads-list">
            {!teacherThread ? (
              <div style={{ padding: "20px", textAlign: "center", color: "#8c8594" }}>
                No assigned teacher
              </div>
            ) : !isMatchingSearch ? (
              <div style={{ padding: "20px", textAlign: "center", color: "#8c8594" }}>
                No matching teacher found
              </div>
            ) : (
              <div
                className={`chat-thread-item ${activeOtherPartyId === teacherThread.otherPartyId ? "active" : ""}`}
                onClick={() => handleSelectConversation(teacherThread.otherPartyId)}
              >
                <div className="chat-avatar">
                  {teacherThread.otherPartyName.charAt(0).toUpperCase()}
                </div>
                <div className="chat-thread-info">
                  <div className="chat-thread-top">
                    <span className="chat-participant-name">{teacherThread.otherPartyName}</span>
                    <span className="chat-last-time">
                      {formatChatMessageTime(teacherThread.lastMessageAt, "list")}
                    </span>
                  </div>
                  <div className="chat-thread-bottom">
                    <span className="chat-last-msg">{teacherThread.lastMessage}</span>
                    {teacherThread.unreadCount > 0 && (
                      <span className="chat-unread-badge">{teacherThread.unreadCount}</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Main Chat Conversation View */}
        {activeThread ? (
          <div className="chat-main">
            <div className="chat-main-header">
              <div className="chat-avatar">
                {activeThread.otherPartyName.charAt(0).toUpperCase()}
              </div>
              <div className="chat-header-info">
                <div className="chat-header-title">{activeThread.otherPartyName}</div>
                <span className="chat-header-role">Teacher</span>
              </div>
            </div>

            <div
              className="chat-messages-body"
              ref={chatContainerRef}
              onScroll={handleScroll}
            >
              {isFetchingOlder && (
                <div className="chat-loading-older">Loading older messages...</div>
              )}
              {activeMessages.length === 0 ? (
                <div className="empty-chat-placeholder">
                  No messages yet. Send a message to start!
                </div>
              ) : (
                activeMessages.map((msg) => {
                  const isSentByStudent = msg.Sender_Type === "Student";
                  return (
                    <div
                      key={msg.id}
                      className={`chat-msg-bubble ${isSentByStudent ? "sent" : "received"}`}
                    >
                      <div>{msg.Message}</div>
                      <div className="chat-msg-time">
                        {formatChatMessageTime(msg.Created_Time, "bubble")}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Single WhatsApp-style Chat Input Bar */}
            <form onSubmit={handleSendMessage} className="chat-input-area">
              <input
                type="text"
                placeholder="Type a message..."
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                className="chat-input-field"
              />
              <Button type="submit" variant="primary" size="medium">
                Send
              </Button>
            </form>
          </div>
        ) : (
          <div className="empty-chat-placeholder">Select a teacher to start chatting</div>
        )}
      </div>
    </div>
  );
};

export default StudentInbox;
